import { useState } from 'react';
import {
  Table, Text, Badge, Select, SimpleGrid, ScrollArea, Box, SegmentedControl,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconBell, IconSpeakerphone, IconCash, IconMailOpened } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { TablePagination } from '../components/TablePagination';
import { TableToolbar } from '../components/TableToolbar';
import { FetchOverlay } from '../components/FetchOverlay';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { TableSkeleton } from '../components/TableSkeleton';
import { StatCard } from '../components/StatCard';
import { SectionCard } from '../components/SectionCard';
import { formatDate } from '../lib/format';

import { NOTIF_TYPES as TYPES, NOTIF_TYPE_META as TYPE_META, NotifType } from '../lib/notifTypes';
import { PREMIUM_UI_ENABLED } from '../lib/features';

/**
 * Filtrda `PREMIUM` turi ko'rsatiladimi.
 *
 * Premium muzlatilganda YANGI bunday bildirishnoma yaralmaydi, shuning uchun tur
 * odatda filtrdan tushadi. LEKIN `NOTIF_TYPES`/`NOTIF_TYPE_META` ATAYLAB tegilmagan —
 * eski PREMIUM yozuvlar jadvalda o'z "Premium" nishoni bilan ko'rinaveradi. Shunday
 * yozuvlar HAQIQATAN bo'lsa (`stats.byType` da soni > 0) tur filtrda qoldiriladi:
 * aks holda panelda ko'rinib turgan, ammo FILTRLAB BO'LMAYDIGAN tur paydo bo'lardi.
 */
const showPremiumFilter = (count: number) => PREMIUM_UI_ENABLED || count > 0;

interface AdminNotif {
  id: string;
  type: NotifType;
  title: string;
  body: string;
  readAt: string | null;
  createdAt: string;
  user?: { id: string; fullName: string; role: string } | null;
}
interface Stats {
  total: number;
  unread: number;
  byType: { type: NotifType; count: number }[];
}

export default function Notifications() {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [typeF, setTypeF] = useState<string>('');
  const [readF, setReadF] = useState<string>('');
  const [sort, setSort] = useState('createdAt:desc');
  // mine — menga kelganlar; others — foydalanuvchilarga yuborilganlar (alohida ko'rinadi)
  const [scope, setScope] = useState<'mine' | 'others'>('mine');

  /* Filtrlar BITTA joyda: jadval ham, eksport ham AYNI obyektni oladi. */
  const filtrlar = { q: qDeb || undefined, type: typeF || undefined, read: readF || undefined, sort, scope };
  const { items, isLoading, fetching, pages, page, setPage, total, limit, setLimit} = usePagedQuery<AdminNotif>(
    'admin-notifications',
    '/notifications/admin',
    filtrlar,
    15,
  );

  const { data: stats } = useQuery({
    queryKey: ['admin-notifications-stats'],
    queryFn: async () => (await api.get<Stats>('/notifications/admin/stats')).data,
    staleTime: 30_000,
  });

  const countByType = new Map((stats?.byType ?? []).map((b) => [b.type, b.count]));
  const typeLabel = (ty: NotifType) => t(`notifType.${ty}`);
  const filterTypes = TYPES.filter((ty) => ty !== 'PREMIUM' || showPremiumFilter(countByType.get(ty) ?? 0));

  const resetTo = (setter: (v: string) => void) => (v: string) => { setter(v); setPage(1); };

  return (
    <>
      <PageHeader
        title={t('pages.notifications.title')}
        action={
          <SegmentedControl
            value={scope}
            onChange={(v) => setScope(v as 'mine' | 'others')}
            data={[
              { value: 'mine', label: t('notif.scopeMine') },
              { value: 'others', label: t('notif.scopeUsers') },
            ]}
          />
        }
      />

      {/* Umumiy ko'rsatkichlar — faqat foydalanuvchilarga yuborilganlar ko'rinishida */}
      {scope === 'others' && (
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        <StatCard icon={IconBell} label={t('notif.total')} value={stats?.total ?? '—'} color="brand" />
        <StatCard icon={IconMailOpened} label={t('notif.unread')} value={stats?.unread ?? '—'} color="red"
          onClick={() => resetTo(setReadF)('false')} />
        <StatCard icon={IconSpeakerphone} label={typeLabel('ANNOUNCEMENT')} value={countByType.get('ANNOUNCEMENT') ?? 0} color="grape"
          onClick={() => resetTo(setTypeF)('ANNOUNCEMENT')} />
        <StatCard icon={IconCash} label={typeLabel('PAYMENT')} value={countByType.get('PAYMENT') ?? 0} color="green"
          onClick={() => resetTo(setTypeF)('PAYMENT')} />
      </SimpleGrid>
      )}

      <TableToolbar
        search={search}
        onSearch={resetTo(setSearch)}
        searchPlaceholder={t('notif.searchPlaceholder')}
        sort={sort}
        onSort={resetTo(setSort)}
        sortOptions={[
          { value: 'createdAt:desc', label: t('notif.sortNew') },
          { value: 'createdAt:asc', label: t('notif.sortOld') },
          { value: 'type:asc', label: t('notif.sortType') },
          { value: 'title:asc', label: t('notif.sortTitle') },
        ]}
      >
        <Select
          placeholder={t('notif.allTypes')}
          data={filterTypes.map((ty) => ({ value: ty, label: `${typeLabel(ty)} (${countByType.get(ty) ?? 0})` }))}
          value={typeF || null}
          onChange={(v) => resetTo(setTypeF)(v ?? '')}
          clearable
          w={190}
        />
        <Select
          placeholder={t('notif.allStatuses')}
          data={[
            { value: 'false', label: t('notif.unread') },
            { value: 'true', label: t('notif.read') },
          ]}
          value={readF || null}
          onChange={(v) => resetTo(setReadF)(v ?? '')}
          clearable
          w={160}
        />
        <TableExport<AdminNotif>
          endpoint="/notifications/admin"
          params={filtrlar}
          filename={t('nav.notifications')}
          ustunlar={[
            { key: 'title', label: t('fields.title') },
            { key: 'user', label: t('notif.recipient'), get: (r) => r.user?.fullName ?? '' },
            { key: 'type', label: t('fields.type') },
            { key: 'createdAt', label: t('fields.date') },
          ]}
          sanaMaydoni={(r) => r.createdAt}
        />
      </TableToolbar>

      {/* Karta doim ko'rinadi — ichida yuklanish/bo'sh holat/jadval almashadi */}
      <SectionCard title={t('common.list')} icon={IconBell} p={0} pos="relative">
        {/* Filtr/sahifa almashganda eski qatorlar ustida yengil yuklanish qatlami */}
        <FetchOverlay visible={fetching} />
        {isLoading ? (
          <TableSkeleton rows={8} cols={5} />
        ) : !items.length ? (
          <EmptyState icon={IconBell} title={t('pages.notifications.empty')} description="" />
        ) : (
          <ScrollArea>
            <Table striped highlightOnHover verticalSpacing="sm" miw={720}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('notif.recipient')}</Table.Th>
                  <Table.Th>{t('notif.type')}</Table.Th>
                  <Table.Th>{t('notif.message')}</Table.Th>
                  <Table.Th>{t('notif.status')}</Table.Th>
                  <Table.Th>{t('notif.date')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((n) => {
                  const meta = TYPE_META[n.type] ?? TYPE_META.SYSTEM;
                  const Ico = meta.icon;
                  return (
                    <Table.Tr key={n.id}>
                      <Table.Td>
                        <Text size="sm" fw={600} lineClamp={1}>{n.user?.fullName ?? '—'}</Text>
                        {n.user?.role && (
                          <Text size="xs" c="dimmed">{t(`roles.${n.user.role}`)}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Badge color={meta.color} variant="light" leftSection={<Ico size={12} />} radius="sm">
                          {typeLabel(n.type)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Box maw={360}>
                          <Text size="sm" fw={600} lineClamp={1}>{n.title}</Text>
                          <Text size="xs" c="dimmed" lineClamp={2}>{n.body}</Text>
                        </Box>
                      </Table.Td>
                      <Table.Td>
                        {n.readAt ? (
                          <Badge color="gray" variant="light" radius="sm">{t('notif.read')}</Badge>
                        ) : (
                          <Badge color="red" variant="filled" radius="sm">{t('notif.unread')}</Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{formatDate(n.createdAt)}</Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </SectionCard>

      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />
    </>
  );
}
